/** @jsx jsx */
import React from 'react';
import { CalendarDay, useMonth, UseMonthProps } from './useDatePicker';
import { Text, Tiles, jsx } from '@front10/base-elements';
import { useIsMobileView } from '@front10/utils';
import { formatDate as fallBackFormat } from '../utils';
import { useDatePickerI18n } from './datePickerI18n';
import { MemoizedDay } from './MemoizedDay';
import { BlackoutDates } from './../../containers/VerticalBookingContainer/blackoutDates';
import Box from '../../elements/Box';
import { DatePickerContext } from './datePickerContext';

interface DayFormat {
  dayFormat?: string;
  allowHoverStyleInRange?: boolean;
}
interface BlackoutDates_ {
  blackoutDatesIntegration?: boolean;
  fetchBlackoutDates?: BlackoutDates | null;
  focusedInput?: string | null;
}

export type MonthProps = DayFormat &
  BlackoutDates_ &
  Pick<UseMonthProps, 'year' | 'month' | 'firstDayOfWeek'>;

export const Month = React.forwardRef<HTMLDivElement, MonthProps>(
  ({ year, month, firstDayOfWeek, dayFormat, allowHoverStyleInRange, ...rest }, ref) => {
    const { months, weekDays, isZh, format = fallBackFormat } = useDatePickerI18n();

    const { days, weekdayLabels, monthLabel } = useMonth({
      year,
      month,
      firstDayOfWeek,
      monthLabelFormat: months
        ? isZh
          ? (date: Date) => `${format(date, ' yyyy年')} ${months[date.getMonth()]}`
          : (date) => `${months[date.getMonth()]} ${date.getFullYear()}`
        : undefined,
      weekdayLabelFormat: weekDays ? (value) => weekDays[value.getDay()] : undefined,
      dayLabelFormat: (date: Date) => {
        return '' + format(date, dayFormat ?? 'd');
      }
    });
    const isMobile = useIsMobileView();
    const monthLabelAlign = isMobile ? 'left' : 'center';

    const { focusedDate } = React.useContext(DatePickerContext);

    const daysToWeeks = (days: CalendarDay[]) => {
      const weeksArray: CalendarDay[][] = [];
      let currentWeek: CalendarDay[] = [];

      days.forEach((day, index) => {
        currentWeek.push(day);

        if (currentWeek.length === 7 || index === days.length - 1) {
          weeksArray.push(currentWeek);
          currentWeek = [];
        }
      });

      // Fill incomplete weeks with empty days
      const lastWeek = weeksArray[weeksArray.length - 1];
      if (lastWeek.length < 7) {
        const emptyDaysCount = 7 - lastWeek.length;
        lastWeek.push(...Array(emptyDaysCount).fill(0));
      }

      return weeksArray;
    };

    const weeks = daysToWeeks(days);

    return (
      <Box
        ref={ref}
        sx={{
          variant: !isMobile
            ? 'layout.datepickerStyles.month'
            : 'layout.datepickerStyles.monthMobile'
        }}
      >
        <Box
          id={monthLabel.replace(' ', '_')}
          sx={{ color: 'text', variant: 'text.fontSize4' }}
          // @ts-expect-error
          css={{
            textAlign: monthLabelAlign,
            margin: '0 0 16px',
            fontWeight: 'normal'
          }}
        >
          <Text aria-label={monthLabel}>{monthLabel}</Text>
        </Box>
        <Tiles
          className="table"
          role="grid"
          selectChildrenWithoutType
          aria-labelledby={monthLabel.replace(' ', '_')}
          sx={{ justifyContent: 'center', variant: 'layout.datepickerStyles.days' }}
        >
          <Tiles
            columns={7}
            sx={{ variant: 'layout.datepickerStyles.weeks' }}
            role="row"
            css={{
              justifyContent: 'center',
              marginBottom: '10px',
              fontSize: '10px'
            }}
          >
            {weekdayLabels.map((dayLabel) => (
              <Box
                sx={{ color: 'text', width: isMobile ? '100%' : 'auto', variant: 'text.fontSize5' }}
                // @ts-expect-error
                css={{ textAlign: 'center', fontWeight: 'bold' }}
                key={dayLabel}
                role="columnheader"
                display="table-cell"
              >
                <Text aria-label={dayLabel}>{dayLabel}</Text>
              </Box>
            ))}
          </Tiles>
          {weeks.map((week, index) => (
            <Box
              className="calendar-week"
              display="flex"
              justifyContent="between"
              key={`month-${week}-${index}`}
              role="row"
            >
              {week.map((day) => {
                return typeof day === 'object' ? (
                  <MemoizedDay
                    date={day.date}
                    key={day.date.toString() + focusedDate?.toString()}
                    dayLabel={day.dayLabel}
                    blackoutDatesIntegration={rest?.blackoutDatesIntegration}
                    fetchBlackoutDates={rest?.fetchBlackoutDates}
                    focusedInput={rest?.focusedInput}
                    allowHoverStyleInRange={allowHoverStyleInRange}
                  />
                ) : (
                  <Box
                    style={{
                      minWidth: '38px',
                      cursor: 'default',
                      width: isMobile ? '100%' : 'auto'
                    }}
                    className="calendar-day"
                    role="gridcell"
                  >
                    <Box key={index}>&nbsp;</Box>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Tiles>
      </Box>
    );
  }
);
